import { Directive, ElementRef, effect, inject, input } from '@angular/core';
import { nativeReceiverOf } from '@4players/odin-plugin-tauri/video/receivers/encoded';
import { nativeRawReceiverOf } from '@4players/odin-plugin-tauri/video/receivers/raw';

/**
 * Whether a stream comes from the native (Tauri) video pipeline. Such a
 * MediaStream is only a tagged placeholder without playable tracks: its
 * frames arrive through a receiver that paints into a `<canvas>` (see
 * {@link VideoMountDirective}) instead of a `<video [srcObject]>`.
 */
export function isNativeVideoStream(stream: MediaStream | null | undefined): boolean {
  return !!(nativeRawReceiverOf(stream) ?? nativeReceiverOf(stream));
}

/**
 * Paints a native video stream into the host `<canvas>` — the native
 * counterpart of `<video [srcObject]>`. Raw previews (own camera / screen)
 * are preferred over encoded frames (remote peers). The canvas takes the
 * frame size as its intrinsic size, so `object-fit`, sizing and transforms
 * written for `<video>` apply unchanged. Several canvases may show the same
 * stream at once.
 *
 * ```html
 * @if (isNativeVideoStream(stream)) {
 *   <canvas [andorVideoMount]="stream"></canvas>
 * } @else {
 *   <video [srcObject]="stream" autoplay playsinline muted></video>
 * }
 * ```
 */
@Directive({
  selector: 'canvas[andorVideoMount]',
  standalone: true,
})
export class VideoMountDirective {
  readonly stream = input.required<MediaStream | null | undefined>({ alias: 'andorVideoMount' });

  private readonly host = inject<ElementRef<HTMLCanvasElement>>(ElementRef);

  constructor() {
    effect((onCleanup) => {
      const stream = this.stream();
      const receiver = nativeRawReceiverOf(stream) ?? nativeReceiverOf(stream);
      if (!receiver) return;
      onCleanup(receiver.mountTo(this.host.nativeElement));
    });
  }
}
